import { useEffect } from 'react'
import type { RefObject } from 'react'

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const getFocusableElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter((element) => {
    const style = window.getComputedStyle(element)
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0
  })

interface UseModalFocusOptions {
  isOpen: boolean
  containerRef: RefObject<HTMLElement>
  initialFocusRef?: RefObject<HTMLElement>
  onClose: () => void
}

/**
 * Keeps modal-like surfaces keyboard-contained while open and restores the
 * trigger focus afterward. The caller owns visual state and backdrop behavior.
 */
export const useModalFocus = ({ isOpen, containerRef, initialFocusRef, onClose }: UseModalFocusOptions) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const container = containerRef.current
    if (!container) {
      return undefined
    }

    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const animationFrame = window.requestAnimationFrame(() => {
      const firstFocusable = initialFocusRef?.current || getFocusableElements(container)[0] || container
      firstFocusable.focus({ preventScroll: true })
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onClose()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const focusableElements = getFocusableElements(container)
      if (focusableElements.length === 0) {
        event.preventDefault()
        container.focus({ preventScroll: true })
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement

      if (event.shiftKey && (activeElement === firstElement || !container.contains(activeElement))) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.cancelAnimationFrame(animationFrame)
      document.removeEventListener('keydown', handleKeyDown, true)
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true })
      }
    }
  }, [containerRef, initialFocusRef, isOpen, onClose])
}
